/**
 * Contracts for module "knowledge". Owner: ai. Mounted at /v1/knowledge.
 */
import { z } from 'zod';

export const KnowledgeSourceTypeSchema = z.enum(['website', 'pdf', 'services', 'pricing', 'faq', 'policy', 'text']);
export const KnowledgeSourceStatusSchema = z.enum(['processing', 'ready', 'failed', 'quarantined', 'deleted']);

/** Text sources (services, pricing, FAQs, policies) or a website page (content captured by the crawler). */
export const CreateTextSourceSchema = z.object({
  type: KnowledgeSourceTypeSchema.exclude(['pdf']),
  title: z.string().min(1).max(200),
  url: z.string().url().max(2000).optional(),
  content: z.string().min(1).max(500_000),
  refreshDays: z.number().int().min(1).max(365).default(30),
});

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const ALLOWED_UPLOAD_TYPES = ['application/pdf', 'text/plain', 'text/markdown', 'text/csv'] as const;

export const KnowledgeSourceSchema = z.object({
  id: z.string(), type: KnowledgeSourceTypeSchema, title: z.string(), status: KnowledgeSourceStatusSchema, statusReason: z.string().nullable(),
  chunkCount: z.number().int(), version: z.number().int(), lastIngestedAt: z.string().nullable(), refreshAfter: z.string().nullable(), stale: z.boolean(),
});

export const ChatQuestionSchema = z.object({ question: z.string().min(2).max(2000) });
export const CreateChatSessionSchema = z.object({ title: z.string().max(200).optional() });
export const CitationSchema = z.object({ id: z.string(), kind: z.enum(['knowledge', 'audit', 'signal']), sourceId: z.string().nullable(), title: z.string() });

// ---------------- website crawler + OCR (Pending Build item 15; flags knowledge.crawler / knowledge.ocr) ----------------

export const KNOWLEDGE_FLAGS = { crawler: 'knowledge.crawler', ocr: 'knowledge.ocr' } as const;

/**
 * Per-plan limits (plan SKU from billing_subscriptions; unknown / no plan -> `none`).
 * crawlPages: max pages fetched per crawl run; ocrPages: max pages OCR'd per document; sites: websites per tenant.
 * OWNER TO CONFIRM: these numbers are engineering placeholders, not approved commercial limits. AI spend is
 * separately capped by the per-tenant AI budget + global cap in @qm/ai (OCR runs through AiRouter).
 */
export const KNOWLEDGE_PLAN_LIMITS: Readonly<Record<string, { crawlPages: number; ocrPages: number; sites: number }>> = {
  none: { crawlPages: 20, ocrPages: 5, sites: 1 },
  plan_starter: { crawlPages: 50, ocrPages: 20, sites: 2 },
  plan_growth: { crawlPages: 200, ocrPages: 50, sites: 5 },
  plan_pipeline: { crawlPages: 200, ocrPages: 50, sites: 5 },
  plan_managed: { crawlPages: 200, ocrPages: 100, sites: 10 },
  plan_enterprise: { crawlPages: 500, ocrPages: 100, sites: 20 },
};
export const knowledgeLimitsFor = (planSku: string | null | undefined) => KNOWLEDGE_PLAN_LIMITS[planSku ?? ''] ?? KNOWLEDGE_PLAN_LIMITS.none!;

export const CRAWL_MAX_DEPTH = 5;
export const RECRAWL_DAY_OPTIONS = [7, 14, 30, 90] as const;

export const CreateSiteSchema = z.object({
  url: z.string().trim().min(4).max(2000),
  title: z.string().trim().min(1).max(200).optional(),
  /** capped by the plan's crawlPages */
  maxPages: z.number().int().min(1).max(500).optional(),
  maxDepth: z.number().int().min(0).max(CRAWL_MAX_DEPTH).default(3),
  /** null = no scheduled re-crawl */
  recrawlDays: z.union([z.literal(7), z.literal(14), z.literal(30), z.literal(90)]).nullable().default(null),
});
export const UpdateSiteSchema = z.object({
  recrawlDays: z.union([z.literal(7), z.literal(14), z.literal(30), z.literal(90)]).nullable(),
});

export const SiteStatusSchema = z.enum(['queued', 'crawling', 'ready', 'failed', 'cancelled', 'deleted']);
export const SitePageStatusSchema = z.enum(['queued', 'indexed', 'unchanged', 'duplicate', 'skipped', 'failed']);
export const OcrStatusSchema = z.enum(['queued', 'running', 'done', 'partial', 'failed']);

export const SiteSchema = z.object({
  id: z.string(), sourceId: z.string(), rootUrl: z.string(), status: SiteStatusSchema, statusReason: z.string().nullable(),
  maxPages: z.number().int(), maxDepth: z.number().int(), recrawlDays: z.number().int().nullable(),
  pages: z.object({ found: z.number().int(), indexed: z.number().int(), unchanged: z.number().int(), skipped: z.number().int(), failed: z.number().int() }),
  startedAt: z.string().nullable(), finishedAt: z.string().nullable(), nextCrawlAt: z.string().nullable(),
});

// ---------------- FAQs: question and answer pairs (owner decision 31 Oct 2026; the WhatsApp AI assistant grounds on them) ----------------
export const MAX_FAQS = 50;
/** Drop control characters except the line feed. */
const stripControl = (s: string) => [...s].filter((ch) => { const c = ch.charCodeAt(0); return c === 10 || (c >= 32 && c !== 127); }).join('');
const faqText = (max: number) => z.string().max(max * 2).transform((s) => stripControl(s).trim()).pipe(z.string().min(1).max(max));
export const FaqItemSchema = z.object({ question: faqText(300), answer: faqText(1000) }).strict();
export type FaqItem = z.infer<typeof FaqItemSchema>;
/** PUT /v1/knowledge/faqs replaces the whole list (owner/admin). */
export const FaqListSchema = z.object({ items: z.array(FaqItemSchema).max(MAX_FAQS) }).strict();
